import type { CreateTeamData } from "@bezoned/shared/queue/create-team-job.schema.js";
import { JobTypes } from "@bezoned/shared/queue/job-type.js";
import type { AppChangelogGetResponse } from "@bezoned/shared/types/api/app/app-changelog-get.types.js";
import type { AppEventCreateBody } from "@bezoned/shared/types/api/app/app-event-create.types.js";
import type { AppFlagListResponse } from "@bezoned/shared/types/api/app/app-flag-list.types.js";
import type { AppInfoGetResponse } from "@bezoned/shared/types/api/app/app-info-get.types.js";
import type { AppOfficePresetListResponse } from "@bezoned/shared/types/api/app/app-office-preset-list.types.js";
import type {
  AppPublicEventCreateBody,
  AppPublicEventCreateResponse,
} from "@bezoned/shared/types/api/app/app-public-event-create.types.js";
import type { AppQueueJobCreateResponse } from "@bezoned/shared/types/api/app/app-queue-job-create.types.js";
import type {
  AppQueueJobGetParams,
  AppQueueJobGetResponse,
} from "@bezoned/shared/types/api/app/app-queue-job-get.types.js";
import type { AppSurveyActiveGetResponse } from "@bezoned/shared/types/api/app/app-survey-active-get.types.js";
import type {
  AppSurveyResponseCreateBody,
  AppSurveyResponseCreateParams,
  AppSurveyResponseCreateResponse,
} from "@bezoned/shared/types/api/app/app-survey-response-create.types.js";
import type {
  ChannelTabListParams,
  ChannelTabListResponse,
} from "@bezoned/shared/types/api/channel/channel-tab-list.types.js";
import type { MeetingRoomJoinParams } from "@bezoned/shared/types/api/meeting-room/meeting-room-join.types.js";
import type {
  TableCallGetBody,
  TableCallGetParams,
  TableCallGetResponse,
} from "@bezoned/shared/types/api/table-call/table-call-get.types.js";
import type {
  TeamAccessCheckParams,
  TeamAccessCheckResponse,
} from "@bezoned/shared/types/api/team/team-access-check.types.js";
import type {
  TeamAvailableChannelsGetParams,
  TeamAvailableChannelsGetResponse,
} from "@bezoned/shared/types/api/team/team-available-channels-get.types.js";
import type {
  TeamChatJoinParams,
  TeamChatJoinResponse,
} from "@bezoned/shared/types/api/team/team-chat-join.types.js";
import type { TeamCreationJobAcknowledgeParams } from "@bezoned/shared/types/api/team/team-creation-job-acknowledge.types.js";
import type { TeamCreationJobListResponse } from "@bezoned/shared/types/api/team/team-creation-job-list.types.js";
import type {
  TeamDeleteParams,
  TeamDeleteResponse,
} from "@bezoned/shared/types/api/team/team-delete.types.js";
import type {
  TeamFluidUpdateBody,
  TeamFluidUpdateParams,
  TeamFluidUpdateResponse,
} from "@bezoned/shared/types/api/team/team-fluid-update.types.js";
import type {
  TeamInstallationStatusGetResponse,
  TeamInstallationStatusGetParams,
} from "@bezoned/shared/types/api/team/team-installation-status-get.types.js";
import type {
  TeamListQuery,
  TeamListResponse,
} from "@bezoned/shared/types/api/team/team-list.types.js";
import type {
  TeamLogoDeleteParams,
  TeamLogoDeleteResponse,
} from "@bezoned/shared/types/api/team/team-logo-delete.types.js";
import type {
  TeamLogoGetUploadUrlBody,
  TeamLogoGetUploadUrlResponse,
} from "@bezoned/shared/types/api/team/team-logo-get-upload-url.types.js";
import type { TeamLogoUpdateResponse } from "@bezoned/shared/types/api/team/team-logo-update.types.js";
import type {
  TeamMemberCreateBody,
  TeamMemberCreateParams,
  TeamMemberCreateResponse,
} from "@bezoned/shared/types/api/team/team-member-create.types.js";
import type { TeamMemberDeleteParams } from "@bezoned/shared/types/api/team/team-member-delete.types.js";
import type {
  TeamMemberListParams,
  TeamMemberListResponse,
} from "@bezoned/shared/types/api/team/team-member-list.types.js";
import type {
  TeamOfficeGetParams,
  TeamOfficeGetQuery,
  TeamOfficeGetResponse,
} from "@bezoned/shared/types/api/team/team-office-get.types.js";
import type {
  TeamOfficePresetUpdateBody,
  TeamOfficePresetUpdateParams,
} from "@bezoned/shared/types/api/team/team-office-preset-update.types.js";
import type {
  TeamOfficeTableUpdateBody,
  TeamOfficeTableUpdateParams,
} from "@bezoned/shared/types/api/team/team-office-table-update.types.js";
import type {
  TeamOfficeUpdateBody,
  TeamOfficeUpdateParams,
} from "@bezoned/shared/types/api/team/team-office-update.types.js";
import type { TeamReorderBody } from "@bezoned/shared/types/api/team/team-reorder.types.js";
import type {
  TeamUpdateBody,
  TeamUpdateParams,
} from "@bezoned/shared/types/api/team/team-update.types.js";
import type { CreateTenantJobTriggerResponse } from "@bezoned/shared/types/api/tenant/create-tenant-job-trigger.types.js";
import type { TenantExistsResponse } from "@bezoned/shared/types/api/tenant/tenant-exists.types.js";
import type { TenantPermissionVersionUpdateResponse } from "@bezoned/shared/types/api/tenant/tenant-permission-version-update.types.js";
import type { TenantRosterGetResponse } from "@bezoned/shared/types/api/tenant/tenant-roster-get.types.js";
import type {
  TenantUserListQuery,
  TenantUserListResponse,
} from "@bezoned/shared/types/api/tenant/tenant-user-list.types.js";
import type {
  UserAvatarMenuGetParams,
  UserAvatarMenuGetResponse,
} from "@bezoned/shared/types/api/user/user-avatar-menu-get.types.js";
import type { UserLicenseGetResponse as SubscriptionGetResponse } from "@bezoned/shared/types/api/user/user-license-get.types.js";
import type {
  UserMessageCreateBody,
  UserMessageCreateParams,
  UserMessageCreateResponse,
} from "@bezoned/shared/types/api/user/user-message-create.types.js";
import type { UserOfficeLeaveBody } from "@bezoned/shared/types/api/user/user-office-leave.types.js";
import type {
  UserPhotoGetParams,
  UserPhotoGetResponse,
} from "@bezoned/shared/types/api/user/user-photo-get.types.js";
import type {
  UserPresenceGetParams,
  UserPresenceGetResponse,
} from "@bezoned/shared/types/api/user/user-presence-get.types.js";
import type {
  UserPresencePingBody,
  UserPresencePingResponse,
} from "@bezoned/shared/types/api/user/user-presence-ping.types.js";
import type { UserPresenceSetBody } from "@bezoned/shared/types/api/user/user-presence-set.types.js";
import type { UserStatusMessageSetBody } from "@bezoned/shared/types/api/user/user-status-message-set.types.js";
import { Effect } from "effect";
import { status } from "http-status";

import { useAuthBridgeStore } from "../features/auth/auth-bridge.store.js";
import { withAuth } from "../features/auth/with-auth.decorator.js";
import type { ApiCallContext } from "../features/auth/with-auth.decorator.js";
import {
  FeatureNotAvailableError,
  TeamOfficeForbiddenError,
} from "../features/office/office.errors.js";
import { STARTER_OFFICE_LIMIT_REACHED_MESSAGE } from "../features/subscription/starter-plan-copy.js";
import { CreateTeamSubmitError } from "../features/team/team-create/create-team-submit-error.js";
import { env } from "../utils/env.js";
import { recordErrorInSentry } from "../utils/init-sentry.js";
import { retryEffect } from "../utils/retry-with-effect.js";

const domain = env.PUBLIC_API_URL;

export class ApiResponseError extends Error {
  readonly status: number;

  constructor(message: string, responseStatus: number) {
    super(message);
    this.name = "ApiResponseError";
    this.status = responseStatus;
  }
}

export const officePresetsGet = withAuth<AppOfficePresetListResponse>(
  async ({ headers }) => {
    const response = await fetch(`${domain}/api/app/office-presets`, {
      headers,
      method: "GET",
    });

    if (!response.ok) {
      throw new Error(`Failed to get office presets: ${response.status}`);
    }

    return response.json();
  }
);

export const appSurveyActiveGet = withAuth<AppSurveyActiveGetResponse>(
  async ({ headers }) => {
    const response = await fetch(`${domain}/api/app/surveys/active`, {
      headers,
      method: "GET",
    });

    if (!response.ok) {
      throw new ApiResponseError(
        `Failed to get active survey: ${response.status}`,
        response.status
      );
    }

    return response.json();
  }
);

export const appSurveyResponseCreate = withAuth<
  AppSurveyResponseCreateParams & AppSurveyResponseCreateBody,
  AppSurveyResponseCreateResponse
>(async ({ headers }, { surveyId, ...body }) => {
  const response = await fetch(
    `${domain}/api/app/surveys/${surveyId}/response`,
    {
      body: JSON.stringify(body),
      headers,
      method: "POST",
    }
  );

  if (!response.ok) {
    throw new ApiResponseError(
      `Failed to save survey response: ${response.status}`,
      response.status
    );
  }

  return response.json();
});

export const getTeamLogoUploadUrl = withAuth<
  TeamLogoGetUploadUrlBody,
  TeamLogoGetUploadUrlResponse
>(async ({ headers }, { mimeType }) => {
  const response = await fetch(`${domain}/api/teams/logo/upload-url`, {
    body: JSON.stringify({ mimeType }),
    headers,
    method: "POST",
  });

  if (!response.ok) {
    throw new Error(`Failed to get logo upload URL: ${response.status}`);
  }

  return response.json();
});

export const uploadTeamLogoToBlob = async (file: File) => {
  const { uploadUrl, uploadToken } = await getTeamLogoUploadUrl({
    mimeType: file.type as TeamLogoGetUploadUrlBody["mimeType"],
  });

  const uploadResponse = await fetch(uploadUrl, {
    body: file,
    headers: {
      "Content-Type": file.type,
      "x-ms-blob-type": "BlockBlob",
    },
    method: "PUT",
  });

  if (!uploadResponse.ok) {
    throw new Error(`Failed to upload logo: ${uploadResponse.status}`);
  }

  return uploadToken;
};

export const teamLogoUpdate = withAuth<
  { msTeamId: string; logoFile: File },
  TeamLogoUpdateResponse
>(async ({ headers }, { msTeamId, logoFile }) => {
  const uploadToken = await uploadTeamLogoToBlob(logoFile);

  const response = await fetch(`${domain}/api/teams/${msTeamId}/logo`, {
    body: JSON.stringify({ uploadToken }),
    headers,
    method: "POST",
  });

  if (!response.ok) {
    throw new Error(`Failed to save team logo: ${response.status}`);
  }

  return response.json();
});

export const teamLogoDelete = withAuth<
  TeamLogoDeleteParams,
  TeamLogoDeleteResponse
>(async ({ headers }, { msTeamId }) => {
  const response = await fetch(`${domain}/api/teams/${msTeamId}/logo`, {
    headers,
    method: "DELETE",
  });

  if (!response.ok) {
    throw new Error(`Failed to delete team logo: ${response.status}`);
  }

  return response.json();
});

export const teamOfficeGet = withAuth<
  TeamOfficeGetQuery & TeamOfficeGetParams,
  TeamOfficeGetResponse
>(async ({ headers }, { msTeamId, enterOffice }) => {
  const url = new URL(`${domain}/api/teams/${msTeamId}/office`);

  if (enterOffice) {
    url.searchParams.set("enterOffice", "true");
  }

  const response = await fetch(url.toString(), {
    headers,
    method: "GET",
  });

  if (response.status === status.FORBIDDEN) {
    throw new TeamOfficeForbiddenError();
  }

  if (response.status === status.PAYMENT_REQUIRED) {
    const errorText = await response.text();
    let message: string | undefined;
    let reason: string | null = null;

    try {
      const errorBody = JSON.parse(errorText) as {
        message?: string;
        reason?: string;
      };

      const { message: parsedMessage, reason: parsedReason } = errorBody;
      message = parsedMessage;
      reason = parsedReason ?? null;
    } catch {
      // Keep the default FeatureNotAvailableError message when the body is not JSON.
    }

    throw new FeatureNotAvailableError(message, reason);
  }

  if (!response.ok) {
    throw new Error(`Failed to get team office: ${response.status}`);
  }

  return response.json();
});

export const getTeamOfficeAvailableChannels = withAuth<
  TeamAvailableChannelsGetParams,
  TeamAvailableChannelsGetResponse
>(async ({ headers }, { msTeamId }) => {
  const response = await fetch(`${domain}/api/teams/${msTeamId}/all-channels`, {
    headers,
    method: "GET",
  });

  if (!response.ok) {
    throw new Error(
      `Failed to get available office channels: ${response.status}`
    );
  }

  return response.json();
});

export const teamOfficeUpdate = withAuth<
  TeamOfficeUpdateParams & TeamOfficeUpdateBody,
  null
>(async ({ headers }, { msTeamId, ...body }) => {
  const response = await fetch(`${domain}/api/teams/${msTeamId}/office`, {
    body: JSON.stringify(body),
    headers,
    method: "PUT",
  });

  if (!response.ok) {
    throw new Error(
      `Failed to update office configuration: ${response.status}`
    );
  }

  return null;
});

export const teamOfficePresetUpdate = withAuth<
  TeamOfficePresetUpdateParams & TeamOfficePresetUpdateBody,
  null
>(async ({ headers }, { msTeamId, presetId, assignments }) => {
  const response = await fetch(`${domain}/api/teams/${msTeamId}/preset`, {
    body: JSON.stringify({
      assignments,
      presetId,
    }),
    headers,
    method: "POST",
  });

  if (!response.ok) {
    throw new Error(`Failed to update office preset: ${response.status}`);
  }

  return null;
});

export const teamOfficeTableUpdate = withAuth<
  TeamOfficeTableUpdateParams & TeamOfficeTableUpdateBody,
  null
>(async ({ headers }, { msTeamId, ...body }) => {
  const response = await fetch(
    `${domain}/api/teams/${msTeamId}/office/tables`,
    {
      body: JSON.stringify(body),
      headers,
      method: "PATCH",
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to update office table: ${response.status}`);
  }

  return null;
});

export const teamChatJoin = withAuth<TeamChatJoinParams, TeamChatJoinResponse>(
  async ({ headers }, params) => {
    const response = await fetch(
      `${domain}/api/teams/${params.msTeamId}/chat${params.tableId ? `/${params.tableId}` : ""}`,
      {
        headers,
        method: "GET",
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to join ACS chat: ${response.status}`);
    }

    const data = await response.json();
    return data as TeamChatJoinResponse;
  }
);

export const teamAccessCheck = withAuth<
  TeamAccessCheckParams,
  TeamAccessCheckResponse
>(async ({ headers }, { msTeamId }) => {
  const response = await fetch(`${domain}/api/teams/${msTeamId}/check`, {
    headers,
    method: "GET",
  });

  if (!response.ok) {
    throw new ApiResponseError(
      "We cannot find the team. Please verify you have given BeZoned RSC permissions.",
      response.status
    );
  }

  return response.json();
});

export const tenantExists = withAuth<TenantExistsResponse>(
  async ({ headers, user }) => {
    const response = await fetch(`${domain}/api/tenant/exists`, {
      headers,
      method: "GET",
    });

    if (!response.ok) {
      recordErrorInSentry(
        new Error(`Failed to check if my tenant exists: ${response.status}`),
        {
          user: {
            email: user.email,
            id: user.id,
            tenantId: user.tenantId,
            username: user.name,
          },
        }
      );

      throw new Error(`Failed to get tenant info: ${response.status}`);
    }

    return response.json();
  }
);

export const teamInstallationStatusGet = withAuth<
  TeamInstallationStatusGetParams["teamId"],
  TeamInstallationStatusGetResponse
>(async ({ headers }, teamId) => {
  const response = await fetch(`${domain}/api/teams/${teamId}/status`, {
    headers,
    method: "GET",
  });

  if (!response.ok) {
    throw new Error(
      `Failed to get team installation status: ${response.status}`
    );
  }

  return response.json();
});

export const tenantPermissionVersionUpdate =
  withAuth<TenantPermissionVersionUpdateResponse>(async ({ headers }) => {
    const response = await fetch(`${domain}/api/tenant/permission-version`, {
      headers,
      method: "PATCH",
    });

    if (!response.ok) {
      throw new Error(
        `Failed to update tenant permission version: ${response.status}`
      );
    }

    return response.json();
  });

export const checkOrganizationPermissions = withAuth<boolean>(
  async ({ headers }) => {
    const response = await fetch(`${domain}/api/tenant/permissions`, {
      headers,
      method: "GET",
    });

    if (response.status >= status.INTERNAL_SERVER_ERROR) {
      throw new Error(
        `Failed to check organization permissions: ${response.status}`
      );
    }

    return response.ok;
  }
);

export const createTenant = withAuth<CreateTenantJobTriggerResponse>(
  async ({ headers }) => {
    const response = await fetch(`${domain}/api/createTenantJobTrigger`, {
      headers,
      method: "POST",
    });

    // 409 = Tenant already exists -> success in this case
    if (response.status !== status.CONFLICT && !response.ok) {
      throw new Error(`Failed to create tenant: ${response.status}`);
    }

    return response.json();
  }
);

export const createTeam = withAuth<CreateTeamData, AppQueueJobCreateResponse>(
  async ({ headers, user }: ApiCallContext, data: CreateTeamData) => {
    const response = await fetch(`${domain}/api/app/queue/jobs`, {
      body: JSON.stringify({
        data: {
          ...data,
          msTenantId: user.tenantId,
        },
        type: JobTypes.CreateTeam,
      }),
      headers,
      method: "POST",
    });

    if (!response.ok) {
      const errorText = await response.text();
      let parsedErrorBody: {
        code?: string;
        details?: { message?: string; path?: string }[];
        message?: string;
        reason?: string;
        type?: string;
      } | null = null;

      try {
        parsedErrorBody = JSON.parse(errorText) as {
          code?: string;
          details?: { message?: string; path?: string }[];
          message?: string;
          reason?: string;
          type?: string;
        };
      } catch {
        // The fallback below handles non-JSON error bodies.
      }

      const nameError = parsedErrorBody?.details?.find(
        (detail) => detail.path === "name" && detail.message
      )?.message;
      const isNameUnavailableError =
        response.status === status.BAD_REQUEST &&
        parsedErrorBody?.message ===
          "The team name is already in use. Please choose a different name.";

      if (nameError || isNameUnavailableError) {
        const message =
          nameError ??
          parsedErrorBody?.message ??
          "Office name is unavailable. Please choose a different name.";

        throw new CreateTeamSubmitError({
          code: "name-unavailable",
          fieldErrors: {
            name: message,
          },
          message,
        });
      }

      if (
        response.status === status.FORBIDDEN &&
        parsedErrorBody?.code === "feature_not_available" &&
        parsedErrorBody.type === "limit_reached" &&
        parsedErrorBody.reason === "starter_office_limit_reached"
      ) {
        throw new CreateTeamSubmitError({
          code: "starter-office-limit-reached",
          message:
            parsedErrorBody.message ?? STARTER_OFFICE_LIMIT_REACHED_MESSAGE,
        });
      }

      recordErrorInSentry(
        new Error(
          `Failed to create team: ${response.status} - ${JSON.stringify(
            parsedErrorBody
          )}`
        ),
        {
          user: {
            email: user.email,
            id: user.id,
            tenantId: user.tenantId,
            username: user.name,
          },
        }
      );

      throw new CreateTeamSubmitError({
        message: "Failed to create office. Please try again.",
      });
    }

    return response.json();
  }
);

export const getTeamCreationJobs = withAuth<TeamCreationJobListResponse>(
  async ({ headers }) => {
    const response = await fetch(`${domain}/api/teams/create-jobs`, {
      headers,
      method: "GET",
    });

    if (!response.ok) {
      throw new Error(`Failed to get create-team jobs: ${response.status}`);
    }

    return response.json();
  }
);

export const acknowledgeTeamCreationJob = withAuth<
  TeamCreationJobAcknowledgeParams,
  null
>(async ({ headers }, { jobId }) => {
  const response = await fetch(
    `${domain}/api/teams/create-jobs/${jobId}/acknowledge`,
    {
      headers,
      method: "POST",
    }
  );

  if (!response.ok) {
    throw new Error(
      `Failed to acknowledge create-team job: ${response.status}`
    );
  }

  return null;
});

export const getCreateTeamJob = withAuth<
  AppQueueJobGetParams,
  AppQueueJobGetResponse
>(async ({ headers }: ApiCallContext, { jobId }) => {
  const response = await fetch(
    `${domain}/api/app/queue/jobs/${jobId}?variant=tenant`,
    {
      headers,
      method: "GET",
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to get create team job: ${response.status}`);
  }

  return response.json();
});

export const recreateTeamFluidContainer = withAuth<
  TeamFluidUpdateParams & TeamFluidUpdateBody,
  { status: number; body: TeamFluidUpdateResponse }
>(async ({ headers, user }, { msTeamId, currentContainerId }) => {
  if (!msTeamId) {
    throw new Error("MS Team ID is required to recreate Fluid container");
  }

  const response = await fetch(`${domain}/api/teams/${msTeamId}/fluid`, {
    body: JSON.stringify({
      currentContainerId,
    }),
    headers: {
      ...headers,
      "Content-Type": "application/json",
    },
    method: "POST",
  });

  if (!response.ok) {
    const errorBody = await response.text();
    recordErrorInSentry(
      new Error(
        `Failed to recreate Fluid container: ${response.status} - ${errorBody}`
      ),
      {
        user: {
          email: user.email,
          id: user.id,
          tenantId: user.tenantId,
          username: user.name,
        },
      }
    );

    throw new Error(
      `Failed to recreate Fluid container: ${response.status} - ${errorBody}`
    );
  }

  const result = await response.json();

  return {
    body: result as TeamFluidUpdateResponse,
    status: response.status,
  };
});

export const joinMeeting = withAuth<
  MeetingRoomJoinParams,
  { checkAgain: boolean; url?: string }
>(async ({ headers }, { meetingRoomId }) => {
  const response = await fetch(
    `${domain}/api/meeting-rooms/${meetingRoomId}/meetings`,
    {
      headers: {
        ...headers,
        "Content-Type": "application/json",
      },
      method: "POST",
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to create or get meeting: ${response.status}`);
  }

  const body = await response.json();

  return {
    checkAgain: response.status === status.ACCEPTED,
    url: body?.url,
  };
});

export const getOrganizationUsers = withAuth<
  TenantUserListQuery,
  TenantUserListResponse
>(async ({ headers }, { search }) => {
  const url = new URL(`${domain}/api/tenant/users`);
  if (search) {
    url.searchParams.set("search", search);
  }

  const response = await fetch(url.toString(), {
    headers,
    method: "GET",
  });

  if (!response.ok) {
    console.error(
      "Failed to get organization users:",
      response.status,
      response.statusText
    );
    return { users: [] };
  }

  return response.json();
});

export const teamMemberCreate = withAuth<
  TeamMemberCreateParams & TeamMemberCreateBody,
  TeamMemberCreateResponse
>(async ({ headers, user }, { msTeamId, msUserIds }) => {
  const response = await fetch(`${domain}/api/teams/${msTeamId}/members`, {
    body: JSON.stringify({
      msUserIds,
    }),
    headers,
    method: "POST",
  });

  if (!response.ok && response.status !== status.MULTI_STATUS) {
    const errorBody = await response.text();
    recordErrorInSentry(
      new Error(
        `Failed to add team members: ${response.status} - ${errorBody}`
      ),
      {
        user: {
          msTeamId,
          msTenantId: user.tenantId,
          msUserIds,
        },
      }
    );

    throw new Error(`Failed to add team members: ${response.status}`);
  }

  return response.json();
});

export const getAvatarMenuInfo = withAuth<
  UserAvatarMenuGetParams,
  UserAvatarMenuGetResponse
>(async ({ headers }, { msUserId }) => {
  const response = await fetch(`${domain}/api/users/${msUserId}/avatar-menu`, {
    headers,
    method: "GET",
  });

  if (!response.ok) {
    throw new Error(`Failed to get avatar menu info: ${response.status}`);
  }

  return response.json();
});

export const sendChatMessage = withAuth<
  UserMessageCreateParams & UserMessageCreateBody,
  UserMessageCreateResponse
>(async ({ headers }, { msUserId, message }) => {
  if (!msUserId) {
    throw new Error("userId is required to send a chat message.");
  }

  if (!message) {
    throw new Error("message is required to send a chat message.");
  }

  const response = await fetch(`${domain}/api/users/${msUserId}/message`, {
    body: JSON.stringify({ message }),
    headers,
    method: "POST",
  });

  if (!response.ok) {
    throw new Error(`Failed to send chat message: ${response.status}`);
  }

  return response.json();
});

export const userOfficeLeave = withAuth<
  [UserOfficeLeaveBody, { keepalive?: boolean; signal?: AbortSignal }],
  null
>(async ({ headers, user }, { msTeamId }, { keepalive, signal }) => {
  const response = await fetch(`${domain}/api/users/${user.id}/office/leave`, {
    body: JSON.stringify({ msTeamId }),
    headers,
    method: "POST",
    ...(keepalive ? { keepalive: true } : {}),
    ...(signal ? { signal } : {}),
  });

  if (!response.ok) {
    throw new Error(`Failed to leave office: ${response.status}`);
  }

  return null;
});

export const appInfoGet = withAuth<AppInfoGetResponse>(async ({ headers }) => {
  const response = await fetch(`${domain}/api/app/info`, {
    headers,
    method: "GET",
  });

  if (!response.ok) {
    throw new Error(`Failed to get app info: ${response.status}`);
  }

  return response.json();
});

export const appEventCreate = withAuth<AppEventCreateBody, null>(
  ({ headers }, body: AppEventCreateBody) =>
    Effect.runPromise(
      retryEffect(
        async () => {
          const response = await fetch(`${domain}/api/app/events`, {
            body: JSON.stringify(body),
            headers,
            method: "POST",
          });

          if (!response.ok) {
            throw new ApiResponseError(
              `Failed to create app event: ${response.status}`,
              response.status
            );
          }

          return null;
        },
        { retries: 1 }
      )
    )
);

export const appPublicEventCreate = async (
  body: AppPublicEventCreateBody
): Promise<AppPublicEventCreateResponse> => {
  const response = await fetch(`${domain}/api/app/public-events`, {
    body: JSON.stringify(body),
    headers: {
      "Content-Type": "application/json",
    },
    method: "POST",
  });

  if (!response.ok) {
    throw new Error(`Failed to create public app event: ${response.status}`);
  }

  return await response.json();
};

export async function errorReportCreate(payload: unknown) {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  try {
    const { getAccessToken } = useAuthBridgeStore.getState();
    const accessToken = await getAccessToken();
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
      headers["X-BeZoned-Auth"] = `Bearer ${accessToken}`;
    }
  } catch {
    // Silently continue without auth headers
  }

  const response = await fetch(`${domain}/api/app/error`, {
    body: JSON.stringify(payload),
    headers,
    method: "POST",
  });

  if (!response.ok) {
    // ? Do not throw another error. We just want to report the issue, not create another one.
    console.error(
      "Failed to report error:",
      response.status,
      response.statusText
    );
  }
}

export const getFeatureFlags = withAuth<AppFlagListResponse>(({ headers }) =>
  Effect.runPromise(
    retryEffect(
      async () => {
        const response = await fetch(`${domain}/api/app/flags`, {
          headers,
          method: "GET",
        });

        if (!response.ok) {
          return {};
        }

        return response.json();
      },
      { retries: 3 }
    )
  )
);

const stripHtmlTags = (text: string) => {
  const tmp = document.createElement("div");
  tmp.innerHTML = text;
  return tmp.textContent || "";
};

export const userPresenceGet = withAuth<
  UserPresenceGetParams,
  UserPresenceGetResponse
>(({ headers, user }, { msUserId }) =>
  Effect.runPromise(
    retryEffect(
      async () => {
        const response = await fetch(
          `${domain}/api/users/${msUserId}/presence`,
          {
            headers,
            method: "GET",
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          recordErrorInSentry(
            new Error(
              `Error in getUserPresence: ${response.status} - ${errorText}`
            ),
            {
              user: {
                msTenantId: user.tenantId,
                msUserId,
                response: errorText,
                status: response.status,
              },
            }
          );

          throw new ApiResponseError(
            `Error in getUserPresence: ${response.status} - ${errorText}`,
            response.status
          );
        }

        const data = await response.json();

        return {
          ...data,
          statusMessage: data.statusMessage
            ? stripHtmlTags(data.statusMessage.trim())
            : "",
        };
      },
      { retries: 3 }
    )
  )
);

export const userPresenceSet = withAuth<
  Pick<UserPresenceSetBody, "availability">,
  null
>(({ headers, user }, { availability }) =>
  Effect.runPromise(
    retryEffect(async () => {
      const response = await fetch(`${domain}/api/users/${user.id}/presence`, {
        body: JSON.stringify({
          availability,
        }),
        headers,
        method: "POST",
      });

      if (!response.ok) {
        const errorText = await response.text();

        recordErrorInSentry(
          new Error(
            `Error in setUserPresence: ${response.status} - ${errorText}`
          ),
          {
            user: {
              msTenantId: user.tenantId,
              msUserId: user.id,
              response: errorText,
              status: response.status,
            },
          }
        );

        throw new ApiResponseError(
          `Error in setUserPresence: ${response.status} - ${errorText}`,
          response.status
        );
      }

      return null;
    })
  )
);

export const channelTabList = withAuth<
  ChannelTabListParams,
  ChannelTabListResponse
>(async ({ headers }, { channelUuid }) => {
  const response = await fetch(`${domain}/api/channels/${channelUuid}/tabs`, {
    headers,
    method: "GET",
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to get channel tabs: ${response.status} - ${errorText}`
    );
  }

  return response.json();
});

export const getUserPhoto = withAuth<
  UserPhotoGetParams,
  UserPhotoGetResponse | null
>(async ({ headers }, { msUserId }) => {
  const response = await fetch(`${domain}/api/users/${msUserId}/photo`, {
    headers,
    method: "GET",
  });

  if (response.status === status.NOT_FOUND) {
    return null;
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new ApiResponseError(
      `Failed to get user photo: ${response.status} - ${errorText}`,
      response.status
    );
  }

  return response.json();
});

export const tableCallJoin = withAuth<
  TableCallGetParams & TableCallGetBody,
  TableCallGetResponse
>(async ({ headers }: ApiCallContext, { tableId, fluidId }) => {
  const response = await fetch(`${domain}/api/tables/${tableId}/call`, {
    body: JSON.stringify({ fluidId }),
    headers,
    method: "POST",
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to join table call: ${response.status} - ${errorText}`
    );
  }

  return response.json();
});

export const subscriptionGet = withAuth<SubscriptionGetResponse>(
  async ({ headers, user }: ApiCallContext) => {
    const response = await fetch(`${domain}/api/users/${user.id}/license`, {
      headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to get user subscription: ${response.status}`);
    }

    return response.json();
  }
);

export const myPresencePing = withAuth<
  UserPresencePingBody,
  UserPresencePingResponse
>(({ headers, user }, data) =>
  Effect.runPromise(
    retryEffect(async () => {
      const response = await fetch(`${domain}/api/users/${user.id}/ping`, {
        body: JSON.stringify({
          availability: data.availability,
          msTeamId: data.msTeamId,
        }),
        headers,
        method: "POST",
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new ApiResponseError(
          `Failed to ping user presence: ${response.status} - ${errorText}`,
          response.status
        );
      }

      const result = await response.json();

      return result as UserPresencePingResponse;
    })
  )
);

export const userStatusMessageSet = withAuth<UserStatusMessageSetBody, null>(
  ({ headers, user }, { statusMessage }) =>
    Effect.runPromise(
      retryEffect(async () => {
        const response = await fetch(
          `${domain}/api/users/${user.id}/status-message`,
          {
            body: JSON.stringify({
              statusMessage,
            }),
            headers,
            method: "POST",
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new ApiResponseError(
            `Failed to set user status message: ${response.status} - ${errorText}`,
            response.status
          );
        }

        return null;
      })
    )
);

export const teamsGet = withAuth<TeamListQuery, TeamListResponse>(
  async ({ headers }: ApiCallContext, data) => {
    const url = new URL(`${domain}/api/teams`);
    if (data?.includeRole) {
      url.searchParams.append("includeRole", "true");
    }

    const response = await fetch(url.toString(), {
      headers,
      method: "GET",
    });

    if (!response.ok) {
      throw new ApiResponseError(
        `Failed to get teams: ${response.status}`,
        response.status
      );
    }

    return response.json();
  }
);

export const teamUpdate = withAuth<TeamUpdateParams & TeamUpdateBody, null>(
  async ({ headers }, { msTeamId, name }) => {
    const response = await fetch(`${domain}/api/teams/${msTeamId}`, {
      body: JSON.stringify({ name }),
      headers,
      method: "PATCH",
    });

    if (!response.ok) {
      throw new Error(`Failed to edit team: ${response.status}`);
    }

    return null;
  }
);

export const teamMemberList = withAuth<
  TeamMemberListParams,
  TeamMemberListResponse
>(async ({ headers }, { msTeamId }) => {
  const url = new URL(`${domain}/api/teams/${msTeamId}/members`);

  const response = await fetch(url.toString(), {
    headers,
    method: "GET",
  });

  if (!response.ok) {
    throw new Error(`Failed to get team members: ${response.status}`);
  }

  return response.json();
});

export const teamMemberDelete = withAuth<TeamMemberDeleteParams, null>(
  async ({ headers }, { msTeamId, msMemberId }) => {
    const response = await fetch(
      `${domain}/api/teams/${msTeamId}/members/${msMemberId}`,
      {
        headers,
        method: "DELETE",
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to delete team member: ${response.status}`);
    }

    return null;
  }
);

export const deleteTeam = withAuth<TeamDeleteParams, TeamDeleteResponse>(
  async ({ headers }, { msTeamId }) => {
    const response = await fetch(`${domain}/api/teams/${msTeamId}`, {
      headers,
      method: "DELETE",
    });

    if (!response.ok) {
      throw new Error(`Failed to delete team: ${response.status}`);
    }

    return response.json();
  }
);

export const teamReorder = withAuth<TeamReorderBody, null>(
  async ({ headers }, { msTeamId, newIndex }) => {
    const response = await fetch(`${domain}/api/teams/order`, {
      body: JSON.stringify({
        msTeamId,
        newIndex,
      }),
      headers,
      method: "PATCH",
    });

    if (!response.ok) {
      throw new Error(`Failed to reorder team: ${response.status}`);
    }

    return null;
  }
);

export const appChangelogGet = withAuth<AppChangelogGetResponse>(
  async ({ headers }) => {
    const response = await fetch(`${domain}/api/app/changelog`, {
      headers,
      method: "GET",
    });

    if (!response.ok) {
      throw new Error(`Failed to get changelog: ${response.status}`);
    }

    return response.json();
  }
);

export const tenantRosterGet = withAuth<TenantRosterGetResponse>(
  async ({ headers }) => {
    const response = await fetch(`${domain}/api/tenant/roster`, {
      headers,
      method: "GET",
    });

    if (!response.ok) {
      throw new Error(`Failed to get tenant roster: ${response.status}`);
    }

    return response.json();
  }
);
